import * as Constants from "../../config/constants/Constants.js";
import { createUrl, catchAPIResponse, catchAPIError } from "./http.js";
import { sessionExpired } from "./authQueries.js";
import { prefsForChat } from "../shared/guestPrefs.js";

/** Keep current user message + up to 10 prior turns (user+assistant each). */
const trimChatHistory = (messages) => {
    const cleaned = (messages || []).filter((m) => m?.role && m?.content);
    const maxMessages = 10 * 2 + 1;
    return cleaned.slice(-maxMessages);
};

/** Local ISO-8601 with timezone offset (for seasonal bartender context). */
const clientDateTimeForChat = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const offsetMin = -d.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMin);
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    return `${local}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

const buildChatBody = (props, messages) => ({
    userId: props.chat.chatUserId,
    messages: trimChatHistory(messages),
    preferences: prefsForChat(props.prefs?.guestPrefs),
    clientDateTime: clientDateTimeForChat(),
});

/**
 * Stream bartender reply via SSE. Calls onChunk(delta) for each token batch.
 * Resolves when the stream completes; rejects/returns null on hard failure.
 */
export const sendChatMessageStream = async (props, messages, { onChunk } = {}) => {
    const url = createUrl({ path: Constants.API_CHAT_STREAM });
    const headers = {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: props.user.userJWT ? `Bearer ${props.user.userJWT}` : "",
        Accept: "text/event-stream",
    };

    let response;
    try {
        response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify(buildChatBody(props, messages)),
        });
    } catch (err) {
        catchAPIError(props, err);
        return null;
    }

    if (!response.ok) {
        let body;
        try {
            body = await response.json();
        } catch {
            body = undefined;
        }
        catchAPIResponse(
            props,
            { ok: false, status: response.status, body },
            { onUnauthorized: () => sessionExpired(props) }
        );
        return null;
    }

    if (!response.body) {
        catchAPIError(props, new Error("Empty stream body"));
        return null;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let fullContent = "";
    let failed = false;

    const handleData = (data) => {
        if (data === "[DONE]") return "done";
        let parsed;
        try {
            parsed = JSON.parse(data);
        } catch {
            return null;
        }
        if (parsed.error) {
            props.showSnackbar?.({
                color: "error",
                duration: 6000,
                message: `Error: ${parsed.error}`,
            });
            return "error";
        }
        if (parsed.content) {
            fullContent += parsed.content;
            onChunk?.(parsed.content, fullContent);
        }
        return null;
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sep;
        while ((sep = buffer.indexOf("\n\n")) >= 0) {
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            for (const line of block.split("\n")) {
                const trimmed = line.trim();
                if (!trimmed.startsWith("data:")) continue;
                const data = trimmed.slice(5).trim();
                const result = handleData(data);
                if (result === "done") {
                    // Let the body finish naturally — reader.cancel() on Windows
                    // forces a TCP reset and noisy uvicorn ConnectionResetError.
                    return { role: "assistant", content: fullContent };
                }
                if (result === "error") {
                    failed = true;
                    try {
                        await reader.cancel();
                    } catch {
                        /* ignore */
                    }
                    return null;
                }
            }
        }
    }

    if (failed) return null;
    return { role: "assistant", content: fullContent };
};
